/**
 * Copyright © 2026 Adnuntius AS.
 */
import {Component, Input, OnChanges, OnInit} from '@angular/core';
import {TranslateService} from '@ngx-translate/core';
import * as _ from 'lodash';
import * as moment from 'moment';

import {progressBarCalculator} from './progress-bar-percentage-component';
import {APP_SHARED} from '../app-shared';

const LOG_1_25 = Math.log(1.25);
const MAX_VELOCITY = 10.0;
const MIN_VELOCITY = -MAX_VELOCITY;

function calculateVelocity(timePercent: number, deliveredPercent: number): number {
  let ratio;
  if (deliveredPercent >= (timePercent - 2) && deliveredPercent <= (timePercent + 2)) {
    ratio = 1.0;
  } else if (timePercent > deliveredPercent) {
    ratio = deliveredPercent / Math.max(0, timePercent - 2);
  } else {
    ratio = deliveredPercent / Math.min(100, timePercent + 2);
  }
  return Math.min(Math.max(Math.log(ratio) / LOG_1_25, MIN_VELOCITY), MAX_VELOCITY);
}

@Component({
  standalone: true,
  imports: [...APP_SHARED],
  selector: 'adn-velocity',
  template: `
    <div>
      <div *ngIf="showLabel" [ngClass]="classStyleValue" style="margin-bottom: 3px;">
        <div class="label" [ngClass]="labelClass">{{ labelText }}</div>
      </div>
      <div *ngIf="showCharts">
        <div *ngFor="let loop of progressPercentageLoopNums; let i = index">
          <div class="small tableProgressBarLine">
            <span class="progress-bar tableProgressBarFill" role="progressbar" [style.width.%]="loop || 0" style="background-color: #a9ceef;">&nbsp;</span>
            <span *ngIf="i === 0" class="tableProgressBarLabel">
              <small>
                <span *ngIf="rawProgressPercentage > displayProgressPercentage">&gt;</span>{{ displayProgressPercentage / 100 | percentage:0:true }} delivery
              </small>
            </span>
          </div>
        </div>
        <div *ngFor="let loop of basePercentageLoopNums; let i = index">
          <div class="small tableProgressBarLine">
            <span class="progress-bar tableProgressBarFill" role="progressbar" [style.width.%]="loop || 0" style="background-color: #daebf8;">&nbsp;</span>
            <span *ngIf="i === 0" class="tableProgressBarLabel">
              <small>{{ basePercentage / 100 | percentage:0:true }} time</small>
            </span>
          </div>
        </div>
      </div>
    </div>
  `
})
export class VelocityComponent implements OnInit, OnChanges {
  @Input() hideNotApplicable: boolean;
  @Input() progressPercentage: number;
  @Input() basePercentage: number;
  @Input() maximum: number;
  @Input() endDate: any;
  @Input() view = '';
  @Input() classStyle = '';
  @Input() velocity: number;
  @Input() computeVelocity: boolean;

  showLabel = false;
  showCharts = false;
  labelClass = 'label-default';
  labelText = '';
  classStyleValue = 'small';
  progressPercentageLoopNums: number[] = [];
  basePercentageLoopNums: number[] = [];
  displayProgressPercentage = 0;
  rawProgressPercentage = 0;

  constructor(private readonly translate: TranslateService) {
  }

  ngOnInit(): void {
    this.updateDisplay();
  }

  ngOnChanges(): void {
    this.updateDisplay();
  }

  private updateDisplay(): void {
    let velocity = this.velocity;
    let endDate = this.endDate;

    if (this.computeVelocity && !_.isFinite(velocity) && this.progressPercentage && this.basePercentage) {
      velocity = calculateVelocity(this.basePercentage, this.progressPercentage);
      endDate = moment.now();
    }

    this.showLabel = false;
    this.showCharts = false;
    this.labelClass = 'label-default';
    this.labelText = '';
    this.classStyleValue = this.classStyle === 'none' ? '' : 'small';
    this.progressPercentageLoopNums = [];
    this.basePercentageLoopNums = [];
    this.displayProgressPercentage = 0;
    this.rawProgressPercentage = this.progressPercentage || 0;

    if (!_.isFinite(velocity)) {
      if (!(this.translationKey() === 'NOT_APPLICABLE' && this.hideNotApplicable)) {
        this.showLabel = this.view === 'FULL' || this.view === 'SLIM';
        this.labelText = this.translateLabel('NOT_APPLICABLE');
      }
      return;
    }

    if (!endDate || !_.isDate(moment(endDate).toDate())) {
      this.showLabel = this.view === 'FULL' || this.view === 'SLIM';
      this.labelText = this.translateLabel('NO_END_DATE');
      return;
    }

    const maximum = this.maximum || 1000;
    this.displayProgressPercentage = Math.min(maximum, this.rawProgressPercentage);
    this.progressPercentageLoopNums = progressBarCalculator(this.displayProgressPercentage);
    this.basePercentageLoopNums = progressBarCalculator(this.basePercentage || 0);

    const rawPercentage = (Math.pow(1.25, velocity) - 1) * 100;
    const roundedPercentage = Math.sign(rawPercentage) * Math.ceil(Math.abs(rawPercentage) / 5) * 5;
    let outputPercentage = roundedPercentage.toString();
    let translation = 'HEALTHY_ON_TARGET';

    if (roundedPercentage > 20) {
      this.labelClass = 'label-warning';
      translation = 'OVER';
    } else if (roundedPercentage < -20) {
      this.labelClass = 'label-danger';
      translation = 'UNDER';
    } else if (roundedPercentage === 0) {
      this.labelClass = 'label-primary';
      translation = 'HEALTHY_ON_TARGET';
    } else {
      this.labelClass = 'label-primary';
      translation = 'HEALTHY';
      outputPercentage = roundedPercentage > 0 ? ('+' + roundedPercentage) : roundedPercentage.toString();
    }
    if (roundedPercentage > 50) {
      this.labelClass = 'label-danger';
    }

    if (this.view === 'FULL' || this.view === 'SLIM') {
      this.showLabel = !(translation === 'NOT_APPLICABLE' && this.hideNotApplicable);
      this.labelText = this.translateLabel(translation, outputPercentage);
    }
    if (this.view === 'FULL' || this.view === 'CHART') {
      this.showCharts = true;
    }
  }

  private translationKey(): string {
    return !_.isFinite(this.velocity) ? 'NOT_APPLICABLE' : '';
  }

  private translateLabel(key: string, percentage = ''): string {
    return this.translate.instant('lineItem.delivery.' + key, {percentage});
  }
}
